<template>
  <DashboardCard
    :title="reportType === 'unpaid' ? 'Open Balances' : 'Paid'"
    :count="items.length"
    :loading="loading"
    :widget-type="widgetType"
    :expanded="expanded"
    class="sc-broker-ranking"
    @update:expanded="$emit('update:expanded', $event)"
  >
    <template #actions>
      <v-menu
        v-if="$vuetify.display.mdAndDown"
        v-model="menu"
        location="start"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <v-btn v-bind="props" icon>
            <v-icon>mdi-filter</v-icon>
          </v-btn>
        </template>
        <v-card flat>
          <v-card-title class="py-1">
            <span>{{ reportType === 'unpaid' ? 'Open Balances' : 'Paid' }} Filters</span>
            <v-spacer />
            <v-btn icon @click="menu = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </v-card>
        <v-divider />
        <v-card class="pa-2 d-flex gap-3 justify-center flex-column" flat>
          <OpenBalancesDateFilter
            v-model:filter-label="filterLabel"
            v-model:date-from="dateFrom"
            v-model:date-to="dateTo"
            v-model:date-toggle="dateToggle"
            v-model:time-period="timePeriod"
            @click:clear="clearFilter"
            @click:apply="applyFilter"
          />
          <v-text-field
            v-model="keyword"
            placeholder="Search by keyword"
            hide-details
            clearable
          />
          <v-select
            v-if="isAdmin"
            label="Select Agent"
            hide-details
            :items="members"
            item-title="full_name"
            item-value="id"
            clearable
            @update:model-value="changeUser"
          />
          <v-btn color="primary" @click="menu = false">Apply</v-btn>
        </v-card>
      </v-menu>
      <div v-else class="d-flex align-center gap-2">
        <OpenBalancesDateFilter
          v-model:filter-label="filterLabel"
          v-model:date-from="dateFrom"
          v-model:date-to="dateTo"
          v-model:date-toggle="dateToggle"
          v-model:time-period="timePeriod"
          @click:clear="clearFilter"
          @click:apply="applyFilter"
        />
        <v-text-field
          v-model="keyword"
          placeholder="Search by keyword"
          hide-details
          style="max-width: 300px"
          clearable
        />
        <v-select
          v-if="isAdmin"
          label="Select Agent"
          hide-details
          :items="members"
          item-title="full_name"
          item-value="id"
          style="max-width: 200px"
          clearable
          @update:model-value="changeUser"
        />
      </div>
    </template>
    <template #content>
      <OpenBalancesTableCard
        v-model="tableOptions"
        :loading="loading"
        :fetch-params="fetchParams"
        :filter-loading="filterLoading"
        :items="items"
      />
    </template>
  </DashboardCard>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { debounce } from 'lodash-es';

import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';
import { LOAD_ACTIVE_MEMBERS } from '@/store/modules/members/members-types';
import { ServiceFactory } from '@/services/ServiceFactory';
import { UserWidgetsEnum } from '@/bundles/Settings/enums/userLayoutEnum';
import { formatDate } from '@/utils/filters';
import { getTableOptions } from '@/bundles/BaseTable/helpers/consts';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import DashboardCard from '@/bundles/Dashboard/components/DashboardCard';
import OpenBalancesDateFilter from '@/bundles/Dashboard/components/filters/OpenBalancesDateFilter.vue';
import OpenBalancesTableCard from '@/bundles/Dashboard/components/OpenBalancesTableCard.vue';

const DealsService = ServiceFactory.get('deals');

export default {
  name: 'BrokerProductionCard',

  components: {
    OpenBalancesTableCard,
    OpenBalancesDateFilter,
    DashboardCard,
  },

  props: {
    height: {
      type: [Number],
      default: 200
    },
    reportType: {
      type: String,
      default: 'unpaid'
    },
    expanded: Boolean,
  },

  emits: ['update:expanded'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: () => ({
    items: [],
    loading: true,
    filterLoading: false,
    keyword: '',
    members: [],
    memberId: null,
    dateToggle: 'all',
    timePeriod: null,
    timePeriodItems: [
      { text: 'This Calendar Month', value: 'month' },
      { text: 'This Calendar Year', value: 'year' },
      { text: 'Date Range', value: 'date_range' },
    ],
    filterLabel: null,
    dateFrom: null,
    dateTo: null,
    menu: false,
    tableOptions: getTableOptions({
      sortBy: ['commission'],
      sortDesc: [true],
      itemsPerPage: 10,
    }),
  }),

  computed: {
    ...mapState({
      token: (state) => state.idToken,
    }),
    isAdmin: function () {
      return this.$store.state.userRoles.includes('super admin') || this.$store.state.userRoles.includes('financial admin');
    },
    widgetType () {
      return UserWidgetsEnum.open_balances;
    },
    fetchParams () {
      const params = {
        sort: this.tableOptions.sortBy[0] || null,
        descending: this.tableOptions.sortDesc[0] || false,
        reportType: this.reportType,
      };
      if (this.keyword) {
        params.keyword = encodeURIComponent(this.keyword);
      }
      if (this.isAdmin && this.memberId) {
        params.member = this.memberId;
      }
      if (this.timePeriod) {
        params.timePeriod = this.timePeriod;
      }
      if (this.timePeriod === 'date_range') {
        if (this.dateFrom) {
          params.dateFrom = this.dateFrom;
        }
        if (this.dateTo) {
          params.dateTo = this.dateTo;
        }
      }
      if (this.dateToggle && this.dateToggle !== 'all') {
        params.dateToggle = this.dateToggle;
      }

      return params;
    }
  },

  watch: {
    keyword: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchWrapperDebounced(this);
      }
    },
    timePeriod: function (newValue, oldValue) {
      if (newValue !== oldValue && newValue === null) {
        this.dateToggle = null;
        this.dateFrom = null;
        this.dateTo = null;
      }
    }
  },

  created: async function () {
    await this.fetchWrapper();
    if (this.isAdmin) {
      try {
        await this.getMembers();
      } catch (error) {
        this.addNotification({ message: error?.message, type: 'error' });
      }
    }
  },

  methods: {
    ...mapActions(StoreModulesNamesEnum.Member, {
      loadActiveMembers: LOAD_ACTIVE_MEMBERS,
    }),
    async changeUser (value) {
      this.memberId = value;
      await this.fetchWrapper();
    },
    async getMembers () {
      const records = (await this.loadActiveMembers()).map(item => ({
        ...item,
        full_name: `${item.profile.first_name} ${item.profile.last_name}`
      }))
      records.sort((a, b) => (a.profile.last_name > b.profile.last_name ? 1 : -1))
      this.members = records
    },
    fetchWrapperDebounced: debounce(function (vm) {
      vm.fetchWrapper();
    }, 300),
    fetchWrapper: async function () {
      try {
        this.filterLoading = true;
        const wrapper = document.querySelector('#common-data-table .v-table__wrapper');
        if (wrapper) {
          document.querySelector('#common-data-table .v-table__wrapper').scrollTop = 0;
        }

        const { data } = await DealsService.brokerDeals({
          token: this.token,
          params: Object.entries(this.fetchParams).map(([key, value]) => ({ name: key, value })),
        });

        const { result: records } = data;
        this.setData({ records });
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.loading = false;
        this.filterLoading = false;
      }
    },
    /**
     * Set data to vue instance
     * @param {array} records
     * */
    setData: function ({ records }) {
      this.items = records.map((item, index) => {
        item.number = index + 1;
        return item;
      });
    },
    applyFilter: function () {
      this.menu = false;
      this.fetchWrapper();
      this.setFilterLabel();
    },
    clearFilter: function () {
      this.dateToggle = 'all';
      this.timePeriod = null;
      this.dateFrom = null;
      this.dateTo = null;
    },
    setFilterLabel: function () {
      let label = '';
      if (this.timePeriod && this.timePeriod !== 'date_range') {
        label += `${this.timePeriod === 'month' ? 'This Calendar Month' : 'This Calendar Year'}`;
      } else if (this.timePeriod) {
        label += this.getFormattedDateRange();
      }
      if (this.dateToggle && this.dateToggle !== 'all') {
        label += `${this.dateToggle === 'paid' ? ' - By Paid Date' : ' - By Due Date' }`;
      }
      this.filterLabel = label.length ? label : null;
    },
    getFormattedDateRange () {
      if (this.dateFrom && this.dateTo) {
        return `(${formatDate(this.dateFrom)} - ${formatDate(this.dateTo)})`;
      }

      if (this.dateFrom) {
        return `From: ${formatDate(this.dateFrom)}`;
      }

      if (this.dateTo) {
        return `To: ${formatDate(this.dateTo)}`;
      }

      return '';
    },
  }
};
</script>
